<script>
  import { Handle, Position } from "@xyflow/svelte";
  import { fly, slide } from "svelte/transition";
  import { uiFeatureStore } from "@stores/uiFeatureStore.js";

  export let data;
  export let isConnectable = false;
  export let id;

  let {
    label,
    icon,
    isStart,
    isEnd,
    color,
    handlemodal,
    position,
    seeIcon = true,
    seeButton = true,
    copyShare,
    copyTime,
    textTime,
    textCount,
    shortcutCount,
    mouseclicks,
    occuranceRate,
    timePerCase,
    totalTime,
    topApp,
    expandActivity,
    editActivity,
    explainActivity,
    onExpand,
    isChild = false,
    actions = [],
    windowSequence = [],
    hasWindowSequence = false,
    previewMode = false,
    optimizationHighlighted = false,
    optimizationVisual = null,
    optimizationClassification = "",
    optimizationSaving = 0,
  } = data;

  const actionsPerPage = 5;
  let isExpanded = false;
  let isWindowSequenceExpanded = false;
  let actionPage = 1;
  let appIconMissing = false;
  let currentTopAppName = "";

  $: isSelected = data.isSelected;
  $: topAppName = getTopAppName(topApp) || getLabelAppName(label);
  $: appIconSrc = getAppIconSrc(topAppName);
  $: appInitial = topAppName ? topAppName.charAt(0).toUpperCase() : "";
  $: if (topAppName !== currentTopAppName) {
    appIconMissing = false;
    currentTopAppName = topAppName;
  }
  $: actionPageCount = Math.max(Math.ceil(actions.length / actionsPerPage), 1);
  $: visibleActions = actions.slice(
    (actionPage - 1) * actionsPerPage,
    actionPage * actionsPerPage,
  ).map(cleanTechnicalText);
  $: displayLabel = sanitizeLabelHtml(label);
  $: nodeFillColor =
    !isStart && !isEnd
      ? optimizationClassification || optimizationSaving
        ? color
        : optimizationVisual?.color || color
      : "";

  $$restProps;

  function handleClick(e) {
    e.preventDefault();
    expandActivity(e, id);
  }

  function handleOpenModal(e) {
    e.preventDefault();
    handlemodal(e, id);
  }

  function handleSettings(e) {
    e.preventDefault();
    e.stopPropagation();
    if (editActivity) editActivity(id, getPrimaryLabelText(label));
  }

  function handleExplain(e) {
    e.preventDefault();
    e.stopPropagation();
    if (explainActivity) explainActivity(id);
  }

  function handleExpand(e) {
    e.preventDefault();
    e.stopPropagation();
    isExpanded = !isExpanded;
    actionPage = 1;
    // Wait for the slide transition (200ms) to complete before re-measuring
    setTimeout(() => {
      if (onExpand) onExpand(id, isExpanded || isWindowSequenceExpanded);
    }, 210);
  }

  function handleWindowSequenceExpand(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!hasWindowSequence) return;
    isWindowSequenceExpanded = !isWindowSequenceExpanded;
    setTimeout(() => {
      if (onExpand) onExpand(id, isExpanded || isWindowSequenceExpanded);
    }, 210);
  }

  function setActionPage(e, page) {
    e.preventDefault();
    e.stopPropagation();
    actionPage = Math.max(1, Math.min(actionPageCount, page));
    setTimeout(() => {
      if (onExpand) onExpand(id, isExpanded);
    }, 0);
  }

  function getTopAppName(value) {
    return value ? value.replace(/\s+\d+%$/, "").trim() : "";
  }

  function getAppIconSrc(appName) {
    if (!appName) return "";
    const name = appName.toLowerCase();
    if (name.includes("outlook")) return "/img/assistant/apps/outlook.svg";
    if (name.includes("excel")) return "/img/assistant/apps/excel.svg";
    if (name.includes("sap")) return "/img/assistant/apps/sap.svg";
    if (name.includes("chrome")) return "/img/programIcons/chrome.svg";
    if (name.includes("edge")) return "/img/assistant/apps/edge.svg";
    if (name.includes("explorer")) return "/img/programIcons/explorer.svg";
    if (name.includes("word")) return "/img/programIcons/winword.svg";
    if (name.includes("acrobat")) return "/img/programIcons/acrobat.svg";
    if (name.includes("powerpoint")) return "/img/programIcons/powerpnt.svg";
    return "";
  }

  function getLabelAppName(value) {
    const tempDivElement = document.createElement("div");
    tempDivElement.innerHTML = sanitizeLabelHtml(value || "").split("<br>")[0];
    return (tempDivElement.textContent || "").trim();
  }

  function cleanTechnicalText(value) {
    const tempTextArea = document.createElement("textarea");
    tempTextArea.innerHTML = value || "";
    return tempTextArea.value
      .replace(/\u00a0/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/[\u200B-\u200D\uFEFF\u00AD]/g, "")
      .replace(/[\u202A-\u202E\u2066-\u2069]/g, "")
      .replace(/\uFFFD/g, "")
      .replace(/<{2,3}\s*([^<>]+?)\s*>{2,3}/g, "$1")
      .replace(/[ \t]+/g, " ");
  }

  function sanitizeLabelHtml(html) {
    const tempDivElement = document.createElement("div");
    tempDivElement.innerHTML = html || "";
    const walker = document.createTreeWalker(
      tempDivElement,
      NodeFilter.SHOW_TEXT,
    );
    let node;
    while ((node = walker.nextNode())) {
      node.nodeValue = cleanTechnicalText(node.nodeValue);
    }
    return tempDivElement.innerHTML;
  }

  function htmlToText(html) {
    const tempDivElement = document.createElement("div");
    tempDivElement.innerHTML = sanitizeLabelHtml(html);
    const parts = tempDivElement.innerHTML.split("<br>");
    const windowTitleAndElement = parts
      .slice(-2)
      .map((part) => {
        const textElement = document.createElement("div");
        textElement.innerHTML = part;
        return cleanTechnicalText(textElement.textContent).trim();
      })
      .join("\n");
    return windowTitleAndElement || "";
  }

  function getPrimaryLabelText(html) {
    const tempDivElement = document.createElement("div");
    tempDivElement.innerHTML = sanitizeLabelHtml(html);
    const firstPart = tempDivElement.innerHTML.split("<br>")[0] || "";
    const textElement = document.createElement("div");
    textElement.innerHTML = firstPart;
    return cleanTechnicalText(textElement.textContent).trim();
  }
</script>

{#if isStart}
  <Handle
    type="source"
    position={Position.Bottom}
    style="background: #555;"
    {isConnectable}
    class="customHandle"
  />
{:else}
  <Handle
    type="target"
    position={Position.Top}
    style="background: #555;"
    {isConnectable}
    class="customHandle"
  />
{/if}
<div class="container">
  <div
    data-node-id={id}
    class="custom-node {isStart || isEnd ? 'start-end-node' : ''}
  {isChild ? 'child-node' : ''}
  {optimizationHighlighted ? 'optimization-highlighted' : ''}
  {optimizationVisual?.fullyAutomated ? 'optimization-automated' : ''}
  {isSelected ? 'selected' : ''} {(isStart || isEnd) && !seeIcon
      ? 'no-icon'
      : ''}"
    style={`background: ${nodeFillColor};`}
    in:fly={{ y: 10, duration: 220 }}
  >
    <div class="node-header">
      {#if seeIcon && (isStart || isEnd)}
        <img
          src={icon}
          alt="icon"
          width="35"
          height="30"
          style={isStart || isEnd ? "filter: invert(1);" : ""}
        />
      {/if}
      {#if !isStart && !isEnd}
        <div class="node-app-icon">
          {#if appIconSrc && !appIconMissing}
            <img
              src={appIconSrc}
              alt={topAppName}
              on:error={() => (appIconMissing = true)}
            />
          {:else}
            <div class="missing-app-icon">{appInitial}</div>
          {/if}
        </div>
      {/if}
      <div class="label-meta">
        <span class="label-row">
          <span class="label-node" title={htmlToText(label)}
            >{@html displayLabel}</span
          >
          {#if !isStart && !isEnd && explainActivity}
            <button
              type="button"
              class="explain-btn"
              aria-label="Explain step with Paxbot"
              title="Explain step with Paxbot"
              on:click={handleExplain}
            >
              <svg viewBox="0 0 18 18" aria-hidden="true">
                <circle cx="9" cy="9" r="7.2" />
                <path d="M9 7.7v4.5" />
                <path d="M9 5.4h.01" />
              </svg>
            </button>
          {/if}
        </span>
        {#if !isStart && !isEnd && previewMode}
          <span class="meta-row">
            {#if textCount !== null}<span>Text: {textCount}x</span>{/if}
            {#if shortcutCount !== null}<span>Shortcuts: {shortcutCount}x</span
              >{/if}
            {#if topApp}<span title={topApp}>Top App: {topApp}</span>{/if}
            {#if mouseclicks !== null}<span>Clicks: {mouseclicks}x</span>{/if}
          </span>
        {:else if !isStart && !isEnd && (occuranceRate !== null || timePerCase || topApp)}
          <span class="meta-row">
            {#if occuranceRate !== null}<span>Occurrence: {occuranceRate}%</span
              >{/if}
            {#if topApp}<span title={topApp}>Top App: {topApp}</span>{/if}
          </span>
          <span class="meta-row">
            {#if timePerCase}<span>Time: {totalTime}</span>{/if}
            {#if timePerCase}<span>Weighted Time: {timePerCase}</span>{/if}
          </span>
        {/if}
      </div>
      {#if !isEnd && !isStart && seeButton}
        {#if isChild}
          {#if optimizationClassification}
            <div class="classification-badge {optimizationClassification}">
              {optimizationClassification === "business-required"
                ? "Required"
                : optimizationClassification === "value-adding"
                  ? "Value"
                  : "Waste"}
            </div>
          {:else if optimizationSaving}
            <div class="savings-badge">-{optimizationSaving}%</div>
          {:else if optimizationVisual}
            <div class="optimization-badge">-{optimizationVisual.reduction}%</div>
          {:else}
            <button
              on:click={handleOpenModal}
              class="icon-btn"
              aria-label="More options"
            >
              <img
                alt="icon button"
                class="more_filled_icon undefined"
                src="/img/ui_icons/more.svg"
                width="30"
                height="30"
              />
            </button>
          {/if}
          {#if actions.length && !optimizationVisual}
            <button
              on:click={handleExpand}
              class="icon-btn expand-btn {isExpanded ? 'expanded' : ''}"
              aria-label="Expand node"
            >
              <img
                alt="icon button"
                class="more_filled_icon undefined"
                src="/img/ui_icons/down_dark.svg"
                width="30"
                height="30"
              />
            </button>
          {/if}
        {:else}
          {#if optimizationClassification}
            <div class="classification-badge {optimizationClassification}">
              {optimizationClassification === "business-required"
                ? "Required"
                : optimizationClassification === "value-adding"
                  ? "Value"
                  : "Waste"}
            </div>
          {:else if optimizationSaving}
            <div class="savings-badge">-{optimizationSaving}%</div>
          {:else if optimizationVisual}
            <div class="optimization-badge">-{optimizationVisual.reduction}%</div>
          {:else}
            <button
              on:click={previewMode ? handleClick : handleWindowSequenceExpand}
              class="icon-btn activity-expand-btn"
              aria-label={previewMode ? "Expand activity" : "Show window titles and UI elements"}
              title={previewMode ? "Expand activity" : hasWindowSequence ? "Show window titles and UI elements" : "No window titles available"}
              disabled={!previewMode && !hasWindowSequence}
            >
              {#if !previewMode && hasWindowSequence}
                <svg
                  class="sequence-toggle-symbol"
                  viewBox="0 0 18 18"
                  aria-hidden="true"
                >
                  <path d="M4.5 9h9" />
                  {#if !isWindowSequenceExpanded}
                    <path d="M9 4.5v9" />
                  {/if}
                </svg>
              {:else}
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2Z" />
                </svg>
              {/if}
            </button>
          {/if}
          {#if !previewMode && !optimizationVisual}
            <button
              on:click={handleExpand}
              class="icon-btn expand-btn {isExpanded ? 'expanded' : ''}"
              aria-label="Expand node"
            >
              <img
                alt="icon button"
                class="more_filled_icon undefined"
                src="/img/ui_icons/down_dark.svg"
                width="30"
                height="30"
              />
            </button>
          {/if}
        {/if}
      {/if}
    </div>

    {#if isWindowSequenceExpanded && !isStart && !isEnd && windowSequence.length}
      <div class="window-sequence-panel" transition:slide|local={{ duration: 200 }}>
        {#each windowSequence as item}
          <div class="window-sequence-row">
            <strong class="window-occurrence-count">{item.occurrences || 1}x</strong>
            <span title={`${item.app ? `${item.app}: ` : ""}${item.windowTitle || ""}`}>
              {item.app ? `${item.app}: ` : ""}{item.windowTitle}
            </span>
            <strong class="window-count">
              {item.count}
              {#if item.elements?.length && $uiFeatureStore.platformFeatures.registrationUiElements}
                <span class="window-count-tooltip">
                  <span class="window-count-tooltip-title">Top Elements</span>
                  {#each item.elements as element}
                    <span>{element.name} ({element.count}x)</span>
                  {/each}
                </span>
              {/if}
            </strong>
          </div>
        {/each}
      </div>
    {/if}

    {#if isExpanded && !isStart && !isEnd}
      <div
        class="metrics-panel"
        class:with-actions={actions.length}
        transition:slide|local={{ duration: 200 }}
      >
        {#if actions.length}
          <div class="action-list">
            {#each visibleActions as action, index}
              <div class="action-row">
                <span>{(actionPage - 1) * actionsPerPage + index + 1}.</span>
                <p>{action}</p>
              </div>
            {/each}
          </div>
          {#if actionPageCount > 1}
            <div class="action-pagination">
              <button
                on:click={(event) => setActionPage(event, actionPage - 1)}
                disabled={actionPage === 1}
              >
                {"<"}
              </button>
              <span>{actionPage} / {actionPageCount}</span>
              <button
                on:click={(event) => setActionPage(event, actionPage + 1)}
                disabled={actionPage === actionPageCount}
              >
                {">"}
              </button>
            </div>
          {/if}
        {/if}
        <button type="button" class="metrics-info" aria-label="Show metric info">i
          <span class="metrics-tooltip">
            <b>Copy Share</b>: Share of executions with copy or paste actions.<br />
            <b>Copy Time</b>: Share of time covered by copy/paste windows.<br />
            <b>Text Time</b>: Share of time covered by text-entry windows.<br />
            <b>Mouseclicks</b>: Number of mouse clicks in this activity.
          </span>
        </button>
        {#if editActivity && !isChild && !previewMode && !optimizationVisual}
          <button
            type="button"
            class="metrics-settings"
            aria-label="Edit activity"
            title="Edit activity"
            on:click={handleSettings}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
              />
              <path
                d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06A1.7 1.7 0 0 0 15 19.36a1.7 1.7 0 0 0-1 .94l-.03.08A2 2 0 0 1 12.14 21h-.28a2 2 0 0 1-1.83-1.22l-.03-.08a1.7 1.7 0 0 0-1-.94 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.64 15a1.7 1.7 0 0 0-.94-1l-.08-.03A2 2 0 0 1 3 12.14v-.28a2 2 0 0 1 1.22-1.83l.08-.03a1.7 1.7 0 0 0 .94-1 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.64a1.7 1.7 0 0 0 1-.94l.03-.08A2 2 0 0 1 11.86 3h.28a2 2 0 0 1 1.83 1.22l.03.08a1.7 1.7 0 0 0 1 .94 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.36 9c.17.4.5.73.94 1l.08.03A2 2 0 0 1 21 11.86v.28a2 2 0 0 1-1.22 1.83l-.08.03a1.7 1.7 0 0 0-.3 1Z"
              />
            </svg>
          </button>
        {/if}
        <div class="metrics-grid">
          <div class="metric-item metric-left">
            <span class="metric-label">Copy Share</span>
            <span class="metric-value">{copyShare ? `${copyShare}%` : "—"}</span
            >
          </div>
          <div class="metric-item metric-right">
            <span class="metric-label">Text Time</span>
            <span class="metric-value">{textTime ? `${textTime}%` : "—"}</span>
          </div>
          <div class="metric-item metric-left">
            <span class="metric-label">Copy Time</span>
            <span class="metric-value">{copyTime ? `${copyTime}%` : "—"}</span>
          </div>
          <div class="metric-item metric-right">
            <span class="metric-label">Mouseclicks</span>
            <span class="metric-value">{mouseclicks ?? "—"}</span>
          </div>
        </div>
      </div>
    {/if}
  </div>
</div>

{#if !isStart && !isEnd}
  <Handle
    type="source"
    position={Position.Bottom}
    style="background: #555; cursor: pointer;"
    {isConnectable}
    class="customHandle"
  />
{/if}

<style>
  .container {
    width: 400px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .custom-node {
    display: flex;
    flex-direction: column;
    max-width: 450px;
    font-size: 14px;
    padding: 12px 16px;
    border: 1px solid rgba(0, 0, 0, 0.55);
    border-radius: 10px;
    gap: 0;
    overflow: hidden;
    position: relative;
    z-index: 2;
  }

  .custom-node:not(.start-end-node) {
    padding: 0;
    border-color: var(--pax-black);
  }

  .custom-node:not(.start-end-node):has(.window-sequence-panel) {
    overflow: visible;
  }

  .custom-node.optimization-highlighted:not(.start-end-node) {
    border-color: #2f80ed;
  }

  .custom-node.optimization-automated:not(.start-end-node) {
    filter: grayscale(0.75);
    opacity: 0.55;
  }

  .classification-badge {
    justify-self: end;
    border-radius: 999px;
    padding: 0.15rem 0.35rem;
    font-size: 0.58rem;
    font-weight: 800;
    line-height: 1;
    color: #1c1c20;
    white-space: nowrap;
  }

  .classification-badge.waste {
    background: #ff9b85;
  }

  .classification-badge.business-required {
    background: #ffe08a;
  }

  .classification-badge.value-adding {
    background: #9debb0;
  }

  .savings-badge {
    justify-self: end;
    border-radius: 999px;
    padding: 0.15rem 0.35rem;
    background: #ffffff;
    border: 1px solid #1c1c20;
    color: #1c1c20;
    font-size: 0.58rem;
    font-weight: 800;
    line-height: 1;
    white-space: nowrap;
  }

  :global(.svelte-flow__node:has(.metrics-panel.with-actions)) {
    z-index: 200 !important;
  }

  :global(.svelte-flow__node:has(.window-sequence-panel)) {
    z-index: 1000 !important;
  }

  :global(.svelte-flow__node:has(.window-count:hover)) {
    z-index: 5000 !important;
  }

  :global(.svelte-flow:has(.window-count:hover) .svelte-flow__edges),
  :global(.svelte-flow:has(.window-count:hover) .svelte-flow__edgelabel-renderer) {
    z-index: 0 !important;
  }

  .node-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
  }

  .custom-node:not(.start-end-node) .node-header {
    display: grid;
    grid-template-columns: 44px minmax(0, 1fr) 42px;
    grid-auto-rows: min-content;
    column-gap: 6px;
    row-gap: 2px;
    align-items: center;
    padding: 6px 0 6px 0;
    background: linear-gradient(
      90deg,
      transparent 0 calc(100% - 43px),
      rgba(0, 0, 0, 0.1) calc(100% - 43px) calc(100% - 42px),
      rgba(255, 255, 255, 0.35) calc(100% - 42px) 100%
    );
  }

  :global(.svelte-flow__node .node_hovered) {
    stroke: #ff5f1f !important;
  }

  .selected {
    border-color: #5c25fa;
  }

  .start-end-node {
    background: #000;
    border: 2px solid #000;
    border-radius: 10px;
    color: #fff;
  }

  :global(div.customHandle) {
    border-radius: 0;
    border: none;
    opacity: 0;
  }

  .label-meta {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
  }

  .custom-node:not(.start-end-node) .label-meta {
    grid-column: 2;
    grid-row: 1 / span 2;
  }

  .label-row {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    min-width: 0;
  }

  .node-app-icon {
    grid-column: 1;
    grid-row: 1 / span 2;
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: center;
    border-right: 1px solid rgba(0, 0, 0, 0.1);
  }

  .node-app-icon img,
  .missing-app-icon {
    width: 28px;
    height: 28px;
    border-radius: 6px;
  }

  .missing-app-icon {
    background: rgba(255, 255, 255, 0.28);
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(0, 0, 0, 0.55);
    font-size: 13px;
    font-weight: 700;
  }

  .label-node {
    display: block;
    min-width: 0;
    max-width: 340px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 1px 0;
    font-size: 15px;
    font-weight: 600;
  }

  .explain-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 1.35rem;
    height: 1.35rem;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: #1c1c20;
    line-height: 1;
    cursor: pointer;
    padding: 0;
  }

  .explain-btn svg {
    display: block;
    width: 1.2rem;
    height: 1.2rem;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.85;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .explain-btn:hover {
    color: #ff5f1f;
  }

  /* .meta-row {
    display: flex;
    gap: 4px;
    font-size: 10px;
    margin-top: 2px;
    font-weight: 600;
  } */

  .meta-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    font-size: 10px;
    font-weight: 600;
    margin-top: 1px;
    padding-bottom: 2px;
    position: relative;
    top: 1px;
  }

  .meta-row span {
    padding: 0.1rem 0.5rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.35);
  }

  .no-icon {
    padding-right: 35px;
    padding-left: 30px;
  }

  .icon-placeholder {
    height: 30px;
    display: inline-block;
  }

  .icon-btn {
    background: none;
    border-radius: 50%;
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    padding: 0;
    transition: background 0.15s;
  }

  .custom-node:not(.start-end-node) .icon-btn {
    grid-column: 3;
    justify-self: center;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: transparent;
    border: none;
  }

  .custom-node:not(.start-end-node) .icon-btn img {
    width: 18px;
    height: 18px;
  }

  .optimization-badge {
    grid-column: 3;
    grid-row: 1 / span 2;
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    box-sizing: border-box;
    padding: 0 0.12rem;
    color: #0b4f93;
    font-size: 0.68rem;
    font-weight: 800;
    white-space: nowrap;
  }

  .custom-node:not(.start-end-node) .activity-expand-btn {
    box-sizing: border-box;
    width: 18px;
    height: 18px;
    min-width: 18px;
    min-height: 18px;
    display: grid;
    place-items: center;
    border: 1px solid #1c1c20;
    border-radius: 50%;
    background: #fff;
    color: #1c1c20;
    line-height: 0;
    padding: 0;
  }

  .custom-node:not(.start-end-node) .activity-expand-btn svg {
    display: block;
    width: 13px;
    height: 13px;
    fill: currentColor;
  }

  .sequence-toggle-symbol {
    display: block;
    width: 14px;
    height: 14px;
    overflow: visible;
  }

  .sequence-toggle-symbol path {
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
  }

  .custom-node:not(.start-end-node) .activity-expand-btn:hover {
    border-color: #ff5f1f;
    background: #ff5f1f;
    color: #1c1c20;
  }

  .custom-node:not(.start-end-node) .activity-expand-btn:disabled {
    cursor: default;
    opacity: 0.35;
    border-color: #1c1c20;
    background: #fff;
    color: #1c1c20;
  }

  .expand-btn {
    transition: transform 0.2s ease;
  }

  .expand-btn.expanded {
    transform: rotate(180deg);
  }

  /* Metrics panel */
  .window-sequence-panel {
    position: relative;
    z-index: 20;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    padding: 7px 10px 8px 12px;
    background: #fff;
    border-top: 1px solid #000;
    border-radius: 0 0 6px 6px;
    overflow: visible;
  }

  .window-sequence-row {
    display: grid;
    grid-template-columns: 2.3rem minmax(0, 1fr) auto;
    gap: 0.5rem;
    align-items: center;
    min-height: 24px;
    width: 100%;
    max-width: 100%;
    border-bottom: 1px solid #dedede;
    color: #111827;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.25;
    position: relative;
    z-index: 1;
    overflow: visible;
  }

  .window-sequence-row:hover,
  .window-sequence-row:has(.window-count:hover) {
    z-index: 50;
  }

  .window-sequence-row:last-child {
    border-bottom: none;
  }

  .window-sequence-row span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .window-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.35rem;
    box-sizing: border-box;
    position: relative;
    z-index: 1;
    border: 1px solid #d1d5db;
    border-radius: 5px;
    padding: 1px 5px;
    background: #fff;
    font-size: 10px;
    font-weight: 700;
    cursor: default;
  }

  .window-occurrence-count {
    justify-self: start;
    min-width: 2rem;
    color: #111827;
    font-size: 10px;
    font-weight: 800;
    line-height: 1.2;
  }

  .window-sequence-row strong:hover {
    z-index: 80;
  }

  .window-count-tooltip {
    display: none;
    position: absolute;
    right: 0;
    top: calc(100% + 0.3rem);
    z-index: 6000;
    width: max-content;
    min-width: 10rem;
    max-width: 18rem;
    padding: 0.45rem 0.55rem;
    border-radius: 0;
    background: #1c1c20;
    color: #fff;
    box-shadow: 0 0.5rem 1.2rem rgba(0, 0, 0, 0.2);
    font-size: 11px;
    font-weight: 500;
    line-height: 1.3;
    text-align: left;
    white-space: normal;
  }

  .window-count-tooltip span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .window-count-tooltip .window-count-tooltip-title {
    margin-bottom: 0.25rem;
    color: #fff;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0;
    text-transform: none;
    opacity: 0.9;
  }

  .window-count:hover .window-count-tooltip,
  .window-count:focus .window-count-tooltip {
    display: block;
  }

  .metrics-panel {
    position: relative;
    z-index: 5;
    width: 100%;
    box-sizing: border-box;
    margin-bottom: 0;
    margin-top: 0;
    padding: 10px 12px 12px 46px;
    background: #fff;
    border-radius: 0 0 6px 6px;
    border-top: 1px solid #000;
  }

  .metric-label {
    color: rgba(0, 0, 0);
  }

  .metric-value {
    font-weight: 600;
  }

  .metrics-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    row-gap: 8px;
    align-items: center;
    position: relative;
  }

  .metrics-panel.with-actions .metrics-grid {
    display: none;
  }

  .metrics-panel.with-actions .metrics-info {
    display: none;
  }

  .metrics-panel.with-actions {
    padding: 8px 10px;
    background: #fff;
    position: relative;
    z-index: 201;
  }

  .metrics-info {
    position: absolute;
    left: 12px;
    top: 8px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 1px solid #1c1c20;
    background: #fff;
    color: #1c1c20;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 9.5px;
    font-weight: 700;
    line-height: 1;
    cursor: help;
  }

  .metrics-settings {
    position: absolute;
    left: 12px;
    top: 28px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 1px solid #1c1c20;
    background: #fff;
    color: #1c1c20;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    cursor: pointer;
  }

  .metrics-settings svg {
    display: block;
    width: 9px;
    height: 9px;
    margin: auto;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .metrics-settings:hover {
    border-color: #ff5f1f;
    color: #ff5f1f;
  }

  .metrics-tooltip {
    display: none;
    position: absolute;
    left: 0;
    bottom: 22px;
    z-index: 10;
    width: 310px;
    padding: 10px 12px;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    background: #fff;
    color: #1c1c20;
    font-size: 9px;
    font-weight: 600;
    line-height: 1.4;
    text-align: left;
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
  }

  .metrics-info:hover .metrics-tooltip,
  .metrics-info:focus .metrics-tooltip {
    display: block;
  }

  .action-list {
    margin: 0;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.35;
    text-align: left;
  }

  .action-row {
    display: grid;
    grid-template-columns: 1.5rem minmax(0, 1fr);
    gap: 0.35rem;
    align-items: baseline;
    padding: 0.35rem 0;
    border-bottom: 1px solid #d9d9d9;
  }

  .action-row span {
    color: #1c1c20;
    display: block;
    font-size: 11px;
    line-height: 1.35;
    text-align: right;
  }

  .action-row p {
    margin: 0;
    font-size: 11px;
    line-height: 1.35;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .action-row:last-child {
    border-bottom: none;
  }

  .action-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    font-size: 11px;
    font-weight: 700;
  }

  .action-pagination button {
    width: 1.4rem;
    height: 1.4rem;
    border: 1px solid #d9d9d9;
    border-radius: 50%;
    background: white;
    cursor: pointer;
  }

  .action-pagination button:disabled {
    opacity: 0.4;
    cursor: default;
  }

  .metrics-grid::after {
    content: "";
    position: absolute;
    left: 50%;
    top: -4px;
    bottom: -4px;
    width: 1px;
    background: rgba(0, 0, 0, 0.15);
    transform: translateX(-50%);
  }

  .metric-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 6px;
    font-size: 10px;
    font-weight: 600;
  }

  .metric-item:nth-child(odd) {
    padding-right: 12px;
  }

  .metric-item:nth-child(even) {
    padding-left: 12px;
  }
</style>
